/**
 * Reusable SEO meta tags component for all pages.
 * Renders title, description, canonical, Open Graph, and Twitter Card meta tags.
 */

import Head from 'next/head';

const SITE_URL = 'https://nexgo.travel';
const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/images/og-image.png`;
const SITE_NAME = 'Nexgo AI';

/** Default meta title for the site (brand). */
export const DEFAULT_META_TITLE =
  'Nexgo AI – Travel Discovery Super App for Authentic Journeys';

/** Default meta description for the site (brand). */
export const DEFAULT_META_DESCRIPTION =
  'Discover the world through stories with Nexgo. AI-crafted travel meets local guidance—plan authentic journeys beyond typical tourist routes.';

export interface SeoHeadProps {
  /** Page title (will be suffixed with " | Nexgo AI" unless disableTitleSuffix is true) */
  title: string;
  /** Meta description (recommended 150–160 chars for SEO) */
  description: string;
  /** Path for canonical URL and og:url (e.g. "/chat", "/privacy-policy") */
  path?: string;
  /** Open Graph / Twitter image URL (defaults to site og-image) */
  image?: string;
  /** If true, add robots noindex,nofollow (e.g. for OAuth callbacks) */
  noIndex?: boolean;
  /** Optional og:type (default: "website") */
  ogType?: string;
  /** If true, use title as-is without appending " | Nexgo AI" */
  disableTitleSuffix?: boolean;
}

function ensureNexgoSuffix(title: string): string {
  const suffix = ' | Nexgo AI';
  return title.trim().endsWith(suffix) ? title : `${title.trim()}${suffix}`;
}

export default function SeoHead({
  title,
  description,
  path = '',
  image = DEFAULT_OG_IMAGE,
  noIndex = false,
  ogType = 'website',
  disableTitleSuffix = false,
}: SeoHeadProps) {
  const fullTitle = disableTitleSuffix
    ? title.trim()
    : ensureNexgoSuffix(title);
  const canonicalUrl = path
    ? `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
    : SITE_URL;
  const imageUrl = image.startsWith('http')
    ? image
    : `${SITE_URL}${image.startsWith('/') ? image : `/${image}`}`;

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name='description' content={description} />
      {path ? <link rel='canonical' href={canonicalUrl} /> : null}
      {noIndex ? (
        <meta name='robots' content='noindex, nofollow' />
      ) : (
        <meta name='robots' content='index, follow' />
      )}

      {/* Open Graph */}
      <meta property='og:title' content={fullTitle} />
      <meta property='og:description' content={description} />
      <meta property='og:type' content={ogType} />
      <meta property='og:url' content={canonicalUrl} />
      <meta property='og:image' content={imageUrl} />
      <meta property='og:site_name' content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={fullTitle} />
      <meta name='twitter:description' content={description} />
      <meta name='twitter:image' content={imageUrl} />
    </Head>
  );
}
